import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { Spinner, Empty, relTime } from '../../components/ui.jsx'
import { ConfirmarBorrado } from '../../components/ConfirmarBorrado.jsx'
import { useAyuda } from '../../lib/ayuda.jsx'
import { AyudaAlmacenamiento } from '../../components/AyudaContrato.jsx'

const fmtBytes = (n) => {
  if (n == null) return '—'
  if (n < 1024) return `${n} B`
  const u = ['KiB', 'MiB', 'GiB', 'TiB']
  let v = n
  let i = -1
  do { v /= 1024; i++ } while (v >= 1024 && i < u.length - 1)
  return `${v.toLocaleString('es', { maximumFractionDigits: 1 })} ${u[i]}`
}

export function Storage() {
  // La parte del contrato que toca a esta página, en el panel de ayuda («?» de la cabecera).
  useAyuda(() => ({ titulo: 'Almacenamiento · el contrato', nodo: <AyudaAlmacenamiento /> }), [])

  const { slug } = useParams()
  const qc = useQueryClient()
  const [prefijo, setPrefijo] = useState('')
  const [aBorrar, setABorrar] = useState(null)
  const [aviso, setAviso] = useState(null)
  const [cuotaAbierta, setCuotaAbierta] = useState(false)
  const fichero = useRef(null)

  const resumen = useQuery({ queryKey: ['storage', slug], queryFn: () => api.storage(slug) })
  const lista = useQuery({
    queryKey: ['storage-ls', slug, prefijo],
    queryFn: () => api.storageList(slug, prefijo),
    enabled: resumen.data?.disponible === true,
  })
  const refrescar = () => {
    qc.invalidateQueries({ queryKey: ['storage', slug] })
    qc.invalidateQueries({ queryKey: ['storage-ls', slug] })
  }

  const crear = useMutation({
    mutationFn: () => api.storageProvision(slug),
    onSuccess: (r) => {
      refrescar()
      // Las S3_* ya están en Dokploy, pero la app no las ve hasta el siguiente despliegue.
      if (r.envAplicado) setAviso('Bucket creado. Las variables S3_* están inyectadas: redespliega la app para que las vea (Variables de entorno → Aplicar y desplegar).')
      else setAviso('Bucket creado, pero el entorno no se pudo aplicar en Dokploy: revisa Variables de entorno.')
    },
  })
  const cambiarCuota = useMutation({
    mutationFn: (gib) => api.setQuota(slug, gib),
    onSuccess: (r) => {
      refrescar()
      setCuotaAbierta(false)
      if (!r.aplicadaEnMinio) setAviso('Cuota guardada, pero no se pudo aplicar en MinIO: el límite real sigue siendo el anterior.')
    },
  })
  const subir = useMutation({
    mutationFn: ({ clave, f }) => api.storageUpload(slug, clave, f),
    onSuccess: refrescar,
  })
  const borrar = useMutation({
    mutationFn: (clave) => api.storageDelete(slug, clave),
    onSuccess: () => { setABorrar(null); refrescar() },
  })

  if (resumen.isPending) return <Spinner />
  if (resumen.isError) {
    return <div className="alert error">{resumen.error.body?.detail ?? 'No se pudo leer el almacenamiento.'}</div>
  }

  const r = resumen.data
  if (!r.disponible) {
    return (
      <>
        <Empty title="Sin almacenamiento">
          {r.motivo}
          {r.puedeCrear && '. El proyecto es anterior al almacenamiento: se crea desde aquí, con la cuota por defecto.'}
        </Empty>
        {r.puedeCrear && (
          <div style={{ textAlign: 'center' }}>
            <button className="btn primary" disabled={crear.isPending} onClick={() => crear.mutate()}>
              {crear.isPending ? 'Creando bucket…' : 'Crear almacenamiento'}
            </button>
            {crear.isError && (
              <div className="alert error" style={{ marginTop: 14 }}>
                {crear.error.body?.detail ?? crear.error.message}
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  const pct = r.cuotaBytes > 0 && r.usadoBytes != null
    ? Math.min(100, Math.round((r.usadoBytes / r.cuotaBytes) * 100))
    : null

  const elegir = () => fichero.current?.click()
  const alElegir = (e) => {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    subir.mutate({ clave: `${prefijo}${f.name}`, f })
  }

  // Migas: cada tramo del prefijo es navegable, como en cualquier explorador.
  const tramos = prefijo.split('/').filter(Boolean)
  const err = subir.error ?? borrar.error

  return (
    <>
      <div className="section-head">
        <div>
          <h1 className="h1">Almacenamiento</h1>
          <p className="lede">
            Bucket <span className="mono">{r.bucket}</span> · {r.objetos ?? 0} objetos ·{' '}
            {fmtBytes(r.usadoBytes)} de {fmtBytes(r.cuotaBytes)}
            {pct != null && ` (${pct}%)`}
          </p>
        </div>
        <div className="toolbar">
          <button className="btn secondary" title="Cuota de almacenamiento" onClick={() => setCuotaAbierta(true)}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ verticalAlign: '-2px', marginRight: 6 }}>
              <path d="M8 10.2A2.2 2.2 0 108 5.8a2.2 2.2 0 000 4.4zM13 8c0-.4 0-.8-.1-1.2l1.5-1.1-1.3-2.2-1.7.7c-.6-.5-1.3-.9-2-1.1L9 1.3H7L6.6 3.1c-.7.2-1.4.6-2 1.1l-1.7-.7-1.3 2.2 1.5 1.1a5 5 0 000 2.4l-1.5 1.1 1.3 2.2 1.7-.7c.6.5 1.3.9 2 1.1L7 14.7h2l.4-1.8c.7-.2 1.4-.6 2-1.1l1.7.7 1.3-2.2-1.5-1.1c.1-.4.1-.8.1-1.2z"
                    stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
            </svg>
            Cuota
          </button>
          {r.puedeEscribir && (
            <>
              <input ref={fichero} type="file" hidden onChange={alElegir} />
              <button className="btn" onClick={elegir} disabled={subir.isPending}>
                {subir.isPending ? 'Subiendo…' : 'Subir fichero'}
              </button>
            </>
          )}
        </div>
      </div>

      {pct != null && (
        <div className="quota-bar" title={`${pct}% de la cuota`}>
          <div className="quota-fill" style={{ width: `${pct}%`, background: pct >= 90 ? 'var(--danger)' : undefined }} />
        </div>
      )}

      {aviso && <div className="alert" style={{ marginBottom: 14 }}>{aviso}</div>}
      {err && <div className="alert error">{err.body?.detail ?? err.message}</div>}

      <div className="crumbs">
        <button className="crumb" onClick={() => setPrefijo('')}>{r.bucket}</button>
        {tramos.map((t, i) => (
          <span key={i}>
            {' / '}
            <button className="crumb" onClick={() => setPrefijo(tramos.slice(0, i + 1).join('/') + '/')}>{t}</button>
          </span>
        ))}
      </div>

      {lista.isPending ? <Spinner /> : lista.isError ? (
        <div className="alert error">{lista.error.body?.detail ?? 'No se pudo listar.'}</div>
      ) : lista.data.entradas.length === 0 ? (
        <Empty title="Vacío">
          {prefijo ? 'No hay nada bajo este prefijo.' : 'Todavía no hay ficheros. La app también puede escribir aquí con sus credenciales S3 (pestaña de variables: S3_*).'}
        </Empty>
      ) : (
        <table className="t">
          <thead>
            <tr><th>Nombre</th><th>Tamaño</th><th>Modificado</th><th /></tr>
          </thead>
          <tbody>
            {lista.data.entradas.map((e) => (
              <tr key={e.ruta}>
                <td>
                  {e.carpeta ? (
                    <button className="crumb" onClick={() => setPrefijo(`${e.ruta}/`)}>📁 {e.nombre}/</button>
                  ) : (
                    <a href={`/api/projects/${slug}/storage/objects/download?clave=${encodeURIComponent(e.ruta)}`}
                       className="mono" download>{e.nombre}</a>
                  )}
                </td>
                <td className="mono">{e.carpeta ? '—' : fmtBytes(e.bytes)}</td>
                <td title={e.fecha ?? ''}>{e.fecha ? relTime(e.fecha) : '—'}</td>
                <td style={{ textAlign: 'right' }}>
                  {!e.carpeta && r.puedeEscribir && (
                    <button className="btn danger sm" onClick={() => setABorrar(e)}>Borrar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <DialogoCuota
        abierto={cuotaAbierta}
        cuotaBytes={r.cuotaBytes}
        usadoBytes={r.usadoBytes}
        puedeCambiar={r.puedeCambiarCuota}
        ocupado={cambiarCuota.isPending}
        error={cambiarCuota.error}
        onCerrar={() => { setCuotaAbierta(false); cambiarCuota.reset() }}
        onGuardar={(gib) => cambiarCuota.mutate(gib)}
      />

      <ConfirmarBorrado
        abierto={aBorrar != null}
        nombre={aBorrar?.nombre ?? ''}
        titulo="Borrar fichero"
        ocupado={borrar.isPending}
        onCancelar={() => setABorrar(null)}
        onConfirmar={() => borrar.mutate(aBorrar.ruta)}
      >
        Se borra <span className="mono">{aBorrar?.ruta}</span> del bucket. No hay papelera:
        si la app lo necesitaba, habrá que volver a generarlo.
      </ConfirmarBorrado>
    </>
  )
}

/** Cuota del bucket. La impone MinIO: también frena a la app escribiendo S3 directamente. */
function DialogoCuota({ abierto, cuotaBytes, usadoBytes, puedeCambiar, ocupado, error, onCerrar, onGuardar }) {
  const ref = useRef(null)
  const [gib, setGib] = useState('')
  const enGib = (cuotaBytes / 1024 ** 3).toFixed(1).replace('.0', '')

  useEffect(() => {
    const d = ref.current
    if (!d) return
    if (abierto && !d.open) { setGib(''); d.showModal() }
    if (!abierto && d.open) d.close()
  }, [abierto])

  return (
    <dialog ref={ref} className="dialogo" onCancel={(e) => { e.preventDefault(); onCerrar?.() }}>
      <form onSubmit={(e) => { e.preventDefault(); if (gib && !ocupado) onGuardar?.(Number(gib)) }}>
        <h2 className="h2" style={{ marginTop: 0 }}>Cuota de almacenamiento</h2>
        <div style={{ color: 'var(--fg-2)', fontSize: 13, lineHeight: 1.55 }}>
          <p style={{ marginTop: 0 }}>
            Límite actual: <strong>{enGib} GiB</strong>
            {usadoBytes != null && <> · en uso {fmtBytes(usadoBytes)}</>}.
          </p>
          <p>
            Lo impone MinIO sobre el bucket, así que también aplica cuando la aplicación escribe
            en S3 con sus propias credenciales. Al llegar al límite, las subidas fallan.
          </p>
          {!puedeCambiar && (
            <p style={{ marginBottom: 0 }}>
              Para ampliarla hace falta un <strong>administrador de la plataforma</strong>: si un
              admin de proyecto pudiera ampliarse su propia cuota, no sería una cuota.
            </p>
          )}
        </div>

        {puedeCambiar && (
          <div className="toolbar" style={{ marginTop: 14 }}>
            <input className="input" type="number" min="0.1" max="500" step="0.5" value={gib}
                   placeholder={enGib} autoFocus style={{ width: 110 }}
                   onChange={(e) => setGib(e.target.value)} />
            <span style={{ fontSize: 13, color: 'var(--fg-2)' }}>GiB (0,1–500)</span>
          </div>
        )}
        {error && (
          <div className="alert error" style={{ marginTop: 12 }}>
            {error.body?.detail ?? error.message}
          </div>
        )}

        <div className="toolbar" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
          <button type="button" className="btn secondary" onClick={onCerrar} disabled={ocupado}>
            Cerrar
          </button>
          {puedeCambiar && (
            <button type="submit" className="btn primary" disabled={!gib || ocupado}>
              {ocupado ? 'Guardando…' : 'Guardar cuota'}
            </button>
          )}
        </div>
      </form>
    </dialog>
  )
}
